/** @jsx jsx */
import React from 'react';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { jsx, Box, Flex, Heading, Text, Link as AnchorLink } from 'theme-ui';
import BlockContent from '@sanity/block-content-to-react';
import { ImageFullWidth } from '../../components';
import CTALink from '../CTALink';
import { serializers } from '../../utils/blockMarks';
import * as colors from '../../theme/colors';
import { FbIcon, IgIcon, TwitterIcon, YtIcon } from '../../images/icons';
import footerBg from '../../images/footer-bg.png';
import useElementOnScreen from '../../hooks/useElementOnScreen';
import { isOpenInNewTab } from '../../utils/helper';
import { mapLocationURL } from '../../assets/js/utils';

export default function Footer(props) {
  const footerData = useStaticQuery(graphql`
    query FooterImgQuery {
      footerBGImg: file(relativePath: { eq: "footer-bg.png" }) {
        id
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED, layout: FIXED)
        }
      }
      footerRaylogo: file(relativePath: { eq: "footer-rayhotel-log.png" }) {
        id
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED, layout: FULL_WIDTH)
        }
      }

      footerNavigationData: sanityFooterNav {
        id
        connect {
          _key
          _type
          _rawConnectItems
        }
        explore {
          _key
          _type
          _rawExploreItems
        }
        externalLinks {
          _key
          _type
          _rawExternalLinksItems
        }
        more {
          _key
          _type
          _rawMoreItems
        }
        title
      }
    }
  `);

  const leedsBadge = "<img alt='We love our LEED certified building' style='width:100px;' src='https://cdn.sanity.io/images/r6duunlc/production/7fba1f465c749c754cdeebd398ec0ed9d74924be-200x100.jpg'/>";
  const aaaBadge = "<img alt='AAA 4 Diamond' style='width:100px;margin-top:10px;' src='https://cdn.sanity.io/images/r6duunlc/production/05d7e2e10afd638181e51e04cedae5a3867c062a-182x31.png'/>"

  const { _rawSiteContactInformation, _rawSocialInfo } = props?.siteSettings;
  const getSocialIcon = (name, styles) => {
    switch (name) {
      case 'facebook':
        return <FbIcon alt={name} sx={styles} />;
      case 'instagram':
        return <IgIcon alt={name} sx={styles} />;
      case 'twitter':
        return <TwitterIcon alt={name} sx={styles} />;
      case 'youtube':
        return <YtIcon alt={name} sx={styles} />;
      default:
        return <FbIcon alt={name} sx={styles} />;
    }
  };
  const renderExploreLinks = (expData) => {
    return (
      <Box sx={footerNavStyles.base}>
        <Heading sx={footerNavStyles.heading}>Explore</Heading>
        {expData?._rawExploreItems.map((expLink) => (
          <Box key={Math.random().toString(36).substring(7)} sx={footerNavStyles.item}>
            {isOpenInNewTab(expLink.link.linkUrl) ? (
              <AnchorLink href={expLink.link.linkUrl} sx={footerNavStyles.link} target="_blank">
                {expLink.label}
              </AnchorLink>
            ) : (
              <Link to={expLink.link.linkUrl} sx={footerNavStyles.link}>
                {expLink.label}
              </Link>
            )}
          </Box>
        ))}
      </Box>
    );
  };
  const renderConnectLinks = (connectData) => {
    return (
      <Box sx={footerNavStyles.base}>
        <Heading sx={footerNavStyles.heading}>Connect</Heading>
        {connectData?._rawConnectItems.map((conLink) => (
          <Box key={Math.random().toString(36).substring(7)} sx={footerNavStyles.item}>
            <AnchorLink key={Math.random().toString(36).substring(7)} href={conLink.link.linkUrl} sx={footerNavStyles.link}>
              {conLink.label}
            </AnchorLink>
          </Box>
        ))}
        {/* Address Info will come from site setting then I remove this comment */}
        <Box sx={footerNavStyles.item}>
          <AnchorLink href={`mailto:${_rawSiteContactInformation?.siteEmail}`} sx={footerNavStyles.anchor}>
            {_rawSiteContactInformation?.siteEmail}
          </AnchorLink>
        </Box>
        <Box sx={footerNavStyles.item}>
          <AnchorLink href={`tel:${_rawSiteContactInformation?.siteContactNumber}`} sx={footerNavStyles.anchor}>
            {_rawSiteContactInformation?.siteContactNumber}
          </AnchorLink>
        </Box>
        <Box sx={footerNavStyles.item}>
          <AnchorLink target="__blank" href={mapLocationURL} sx={footerNavStyles.link}>
            <BlockContent blocks={_rawSiteContactInformation?.siteAddress} serializers={serializers} />
          </AnchorLink>
        </Box>
      </Box>
    );
  };
  const renderSocialLinks = () => {
    return (
      <Box sx={footerNavStyles.social}>
        <AnchorLink
            key={Math.random().toString(36).substring(7)}
            href="https://www.youtube.com/@therayhoteldelraybeach8284"
            target="_blank"
            sx={footerNavStyles.socialLinks}
            rel="noreferrer"
        >
          {getSocialIcon('youtube', footerNavStyles.socialIcon)}
        </AnchorLink>
        {_rawSocialInfo.map((linkItem) => (
          <AnchorLink
            key={Math.random().toString(36).substring(7)}
            href={linkItem.pageLink}
            target="_blank"
            sx={footerNavStyles.socialLinks}
            rel="noreferrer"
          >
            {getSocialIcon(linkItem.socialPlatform, footerNavStyles.socialIcon)}
          </AnchorLink>
        ))}
      </Box>
    );
  };
  const renderMoreLinks = (moreLinksList) => {
    return (
      <Box sx={footerNavStyles.base}>
        <Heading sx={footerNavStyles.heading}>More</Heading>
        {moreLinksList?._rawMoreItems.map((moreLink) => (
          <Box key={Math.random().toString(36).substring(7)} sx={footerNavStyles.item}>
            {isOpenInNewTab(moreLink.link.linkUrl) ? (
              <AnchorLink key={Math.random().toString(36).substring(7)} href={moreLink.link.linkUrl} sx={footerNavStyles.link}>
                {moreLink.label}
              </AnchorLink>
            ) : (
              <Link key={Math.random().toString(36).substring(7)} to={moreLink.link.linkUrl} sx={footerNavStyles.link}>
                {moreLink.label}
              </Link>
            )}
          </Box>
        ))}
        <Box dangerouslySetInnerHTML={{__html: leedsBadge }}>
        </Box>
        <Box dangerouslySetInnerHTML={{__html: aaaBadge }}>
        </Box>
        {_rawSocialInfo?.length > 0 && renderSocialLinks()}
      </Box>
    );
  };
  const renderAmenities = () => {
    let amenitiesHtml = "<b>AMENITIES</b>";

    amenitiesHtml += "<style>";
    amenitiesHtml += ".amenitiesfootercontainer { display: flex;flex-wrap: wrap; width:100%;margin-top:15px }";
    amenitiesHtml += ".amenitiesfooter { width:220px; margin:5px 0px; }";
    amenitiesHtml += ".amenitiesfootertext { margin-left:40px;position:relative;top:3px; }";
    amenitiesHtml += ".amentitiesfooterbutton { margin-top:10px;width:100px;border: 1px solid #ffffff;text-align:center;display:block;padding:5px;text-decoration:none;color:#ffffff;}";
    amenitiesHtml += "@media screen and (max-width: 1023px) {";
    amenitiesHtml += ".amenitiesfootercontainer { justify-content: center; padding:0px 20px; }";
    amenitiesHtml += ".amenitiesfooter { width:190px; margin-left:20px;margin-right:20px; }";
    amenitiesHtml += ".amenitiesfootertext { margin-left:20px;position:relative;top:3px; }";
    amenitiesHtml += ".amentitiesfooterbutton { display:block; margin:20px auto; }";
    amenitiesHtml += "}";
    amenitiesHtml += "</style>";

    amenitiesHtml += "<div class='amenitiesfootercontainer'>";

    amenitiesHtml += "<div class='amenitiesfooter'>";
    amenitiesHtml += "<div style='float:left;'><svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" class=\"injected-svg brand-ou:text-primary brand-lx:text-brand\" data-src=\"/modules/assets/svgs/icons/regular/pet-friendly.svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" role=\"img\" aria-hidden=\"true\" fill=\"currentColor\" width=\"28\" height=\"28\" data-testid=\"icon-pet-friendly\"><path d=\"m0 0v24h24v-24z\" fill=\"none\"></path><path d=\"m11.06 8.5c.11 0 .22-.04.31-.11.22-.17.25-.49.08-.7l-3.17-4c-.17-.22-.49-.25-.7-.08-.22.17-.25.49-.08.7l3.17 4c.1.12.24.19.39.19z\"></path><path d=\"m12 16.5c-1.38 0-1.82-.22-2.28-.45-.54-.27-1.11-.55-2.72-.55-.23 0-.43.16-.48.38l-.91 3.62h-.11v-5.4c0-1.5 1.25-1.6 1.5-1.6h4c.28 0 .5-.22.5-.5s-.22-.5-.5-.5h-4c-.35 0-.82.09-1.26.33-1.2-1.21-1.24-2.82-1.24-2.83 0-.27-.23-.5-.5-.5-.28 0-.5.22-.5.5 0 .09.04 1.98 1.49 3.49-.29.39-.49.91-.49 1.61v5.9c0 .28.22.5.5.5h1c.23 0 .43-.16.48-.38l.9-3.61c1.08.04 1.47.23 1.89.44.54.27 1.11.55 2.72.55.28 0 .5-.22.5-.5s-.22-.5-.5-.5z\"></path><path d=\"m20 7.5h-2.32c-.38-.3-1.42-1-2.68-1-2.04 0-3.42 1.97-3.42 3.81 0 .59.15 1.11.45 1.49.34.45.85.69 1.47.69.1 0 .18-.02.28-.03 1.09 1.01 2.18 1.03 2.22 1.03.02 0 .04 0 .06-.01-.3.83-.76 1.81-1.42 2.52-.09.09-.14.21-.14.34v3.15h-.11l-.91-3.62c-.07-.27-.33-.43-.61-.36-.27.07-.43.34-.36.61l1 4c.06.22.26.38.49.38h1c.28 0 .5-.22.5-.5v-3.46c1.22-1.4 1.78-3.49 1.94-4.2l1.78-.89s.09-.06.13-.09c.05-.05 1.15-1.17 1.15-3.35 0-.28-.22-.5-.5-.5zm-7.18 3.7c-.16-.21-.24-.52-.24-.89 0-.7.32-1.6.93-2.19 0 .03.02.07.04.1.46.91.94 2.45.58 3.02-.04.07-.16.25-.64.25-.3 0-.52-.1-.68-.3zm5.87-.61-1.92.96c-.14.07-.24.2-.27.36 0 0-.06.3-.18.73-.09-.08-.2-.13-.32-.14-.03 0-.56-.03-1.21-.48.08-.08.14-.16.19-.24.78-1.27-.4-3.72-.54-4-.03-.06-.08-.12-.13-.16.21-.07.44-.11.68-.11 1.17 0 2.16.87 2.17.87.09.08.21.13.33.13h1.98c-.12 1.22-.63 1.91-.78 2.09z\"></path></svg></div>";
    amenitiesHtml += "<div class='amenitiesfootertext' style=''>Pet Friendly</div>";
    amenitiesHtml += "</div>";

    amenitiesHtml += "<div class='amenitiesfooter'>";
    amenitiesHtml += "<div style='float:left;'><svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" class=\"injected-svg brand-ou:text-primary brand-lx:text-brand\" data-src=\"/modules/assets/svgs/icons/regular/connecting-rooms.svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" role=\"img\" aria-hidden=\"true\" fill=\"currentColor\" width=\"28\" height=\"28\" data-testid=\"icon-connecting-rooms\"><path d=\"m0 0v24h24v-24z\" fill=\"none\"></path><path d=\"m12 3.5c-4.69 0-8.5 3.81-8.5 8.5s3.81 8.5 8.5 8.5 8.5-3.81 8.5-8.5-3.81-8.5-8.5-8.5zm0 16c-4.14 0-7.5-3.36-7.5-7.5s3.36-7.5 7.5-7.5 7.5 3.36 7.5 7.5-3.36 7.5-7.5 7.5z\"></path><path d=\"m12.33 8.21-.69.69c-.2.2-.2.51 0 .71s.51.2.71 0l.69-.69c.56-.56 1.48-.56 2.04 0s.56 1.48 0 2.04l-1.37 1.37c-.17.17-.39.3-.65.37-.51.13-1.03 0-1.39-.37-.2-.2-.51-.2-.71 0s-.2.51 0 .71c.46.46 1.08.71 1.72.71.21 0 .42-.03.63-.08.43-.11.8-.32 1.1-.63l1.37-1.37c.95-.95.95-2.5 0-3.45s-2.5-.95-3.45 0z\"></path><path d=\"m11.65 14.39-.69.69c-.56.56-1.48.56-2.04 0s-.56-1.48 0-2.04l1.37-1.37c.18-.17.4-.3.65-.37.51-.13 1.03 0 1.39.37.2.2.51.2.71 0s.2-.51 0-.71c-.61-.61-1.51-.85-2.35-.63-.43.11-.8.32-1.1.63l-1.37 1.37c-.95.95-.95 2.5 0 3.45.48.48 1.1.71 1.73.71s1.25-.24 1.73-.71l.69-.69c.2-.2.2-.51 0-.71s-.51-.2-.71 0z\"></path></svg></div>";
    amenitiesHtml += "<div class='amenitiesfootertext'>Connecting Rooms</div>";
    amenitiesHtml += "</div>";

    amenitiesHtml += "<div class='amenitiesfooter'>";
    amenitiesHtml += "<div style='float:left;'><svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" class=\"injected-svg brand-ou:text-primary brand-lx:text-brand\" data-src=\"/modules/assets/svgs/icons/regular/meeting.svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" role=\"img\" aria-hidden=\"true\" fill=\"currentColor\" width=\"28\" height=\"28\" data-testid=\"icon-meeting\"><path d=\"m0 0v24h24v-24z\" fill=\"none\"></path><path d=\"m12 8.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0-3c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z\"></path><path d=\"m17.5 8.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0-3c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z\"></path><path d=\"m8.5 6.5c0-1.1-.9-2-2-2s-2 .9-2 2 .9 2 2 2 2-.9 2-2zm-3 0c0-.55.45-1 1-1s1 .45 1 1-.45 1-1 1-1-.45-1-1z\"></path><path d=\"m18 9c-1.16 0-1.64 1.63-2.06 3.08-.1.34-.19.66-.28.92h-1.16v-1c0-1.38-1.12-2.5-2.5-2.5s-2.5 1.12-2.5 2.5v1h-1.13c-.09-.26-.19-.57-.28-.9-.43-1.45-.92-3.1-2.09-3.1-1.38 0-2.5 1.12-2.5 2.5v4c0 .87.52 2.5 2.5 2.5h1.66l.87 2.19c.08.2.27.31.46.31.06 0 .12-.01.19-.04.26-.1.38-.39.28-.65l-1-2.5c-.08-.19-.26-.31-.46-.31h-2c-1.41 0-1.5-1.25-1.5-1.5v-4c0-.83.67-1.5 1.5-1.5.42 0 .92 1.67 1.13 2.38.06.22.13.42.19.62h-.32c-.28 0-.5.22-.5.5s.22.5.5.5h10c.28 0 .5-.22.5-.5s-.22-.5-.5-.5h-.29c.06-.21.13-.42.19-.65.2-.7.68-2.35 1.09-2.35.83 0 1.5.67 1.5 1.5v4c0 .25-.09 1.5-1.5 1.5h-2c-.2 0-.39.12-.46.31l-1 2.5c-.1.26.02.55.28.65.06.02.12.04.19.04.2 0 .39-.12.46-.31l.88-2.19h1.66c1.98 0 2.5-1.63 2.5-2.5v-4c0-1.38-1.12-2.5-2.5-2.5zm-7.5 3c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v1h-3z\"></path><path d=\"m11 20.5c.06 0 .11 0 .17-.03.26-.09.4-.38.3-.64l-1.23-3.43c-.17-.7-.77-1-1.12-1.08l-2.04-.32c-.28-.04-.53.14-.57.42-.04.27.14.53.42.57l2.01.32s.27.07.35.38l1.25 3.48c.07.2.27.33.47.33z\"></path><path d=\"m15.04 16.32 2.04-.32c.27-.04.46-.3.42-.57s-.31-.46-.57-.42l-2.06.33c-.33.07-.93.37-1.08 1.02l-1.25 3.48c-.09.26.04.55.3.64.06.02.11.03.17.03.21 0 .4-.13.47-.33l1.27-3.53c.06-.25.31-.32.31-.32z\"></path></svg></div>";
    amenitiesHtml += "<div class='amenitiesfootertext'>Meeting Rooms</div>";
    amenitiesHtml += "</div>";

    amenitiesHtml += "<div class='amenitiesfooter'>";
    amenitiesHtml += "<div style='float:left;'><svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" class=\"injected-svg brand-ou:text-primary brand-lx:text-brand\" data-src=\"/modules/assets/svgs/icons/regular/streaming-tv.svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" role=\"img\" aria-hidden=\"true\" fill=\"currentColor\" width=\"28\" height=\"28\" data-testid=\"icon-streaming-tv\"><path d=\"m0 0v24h24v-24z\" fill=\"none\"></path><path d=\"m15 18.5h-6c-.28 0-.5.22-.5.5s.22.5.5.5h6c.28 0 .5-.22.5-.5s-.22-.5-.5-.5z\"></path><path d=\"m19 6.5h-14c-.83 0-1.5.67-1.5 1.5v8c0 .83.67 1.5 1.5 1.5h14c.83 0 1.5-.67 1.5-1.5v-8c0-.83-.67-1.5-1.5-1.5zm.5 9.5c0 .28-.22.5-.5.5h-14c-.28 0-.5-.22-.5-.5v-8c0-.28.22-.5.5-.5h14c.28 0 .5.22.5.5z\"></path><circle cx=\"12\" cy=\"12\" r=\".75\"></circle><path d=\"m10.44 10.23c-.2-.2-.51-.2-.71 0-.47.47-.73 1.1-.73 1.77s.26 1.3.73 1.77c.1.1.23.15.35.15s.26-.05.35-.15c.19-.2.19-.51 0-.71-.28-.28-.44-.66-.44-1.06s.16-.78.44-1.06c.2-.2.2-.51 0-.71z\"></path><path d=\"m14.27 10.23c-.2-.19-.51-.19-.71 0-.19.2-.19.51 0 .71.28.28.44.66.44 1.06s-.16.78-.44 1.06c-.2.2-.2.51 0 .71.1.1.23.15.35.15s.26-.05.35-.15c.47-.47.73-1.1.73-1.77s-.26-1.3-.73-1.77z\"></path><path d=\"m15.12 9.17c-.2.2-.2.51 0 .71.57.57.88 1.32.88 2.12s-.31 1.55-.88 2.12c-.2.19-.2.51 0 .71.1.1.23.15.35.15s.26-.05.35-.15c.76-.75 1.17-1.76 1.17-2.83s-.42-2.07-1.17-2.83c-.2-.2-.51-.2-.71 0z\"></path><path d=\"m8 12c0-.8.31-1.55.88-2.12.2-.19.2-.51 0-.71s-.51-.2-.71 0c-.76.75-1.17 1.76-1.17 2.83s.42 2.07 1.17 2.83c.1.1.23.15.35.15s.26-.05.35-.15c.19-.2.19-.51 0-.71-.57-.57-.88-1.32-.88-2.12z\"></path></svg></div>";
    amenitiesHtml += "<div class='amenitiesfootertext'>Streaming TV</div>";
    amenitiesHtml += "</div>";

    amenitiesHtml += "<div class='amenitiesfooter'>";
    amenitiesHtml += "<div style='float:left;'><svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" class=\"injected-svg brand-ou:text-primary brand-lx:text-brand\" data-src=\"/modules/assets/svgs/icons/regular/outdoor-pool.svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" role=\"img\" aria-hidden=\"true\" fill=\"currentColor\" width=\"28\" height=\"28\" data-testid=\"icon-outdoor-pool\"><path d=\"m0 0v24h24v-24z\" fill=\"none\"></path><path d=\"m17 12.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0-3c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z\"></path><path d=\"m21.3 16.6c-.1-.07-2.26-1.62-4.85-1.79 0-.02-.01-.05-.03-.07l-3.94-6.3c-.37-.59-.95-1.01-1.6-1.15-.59-.13-1.17-.02-1.66.3l-3.67 2.45c-.32.22-.55.54-.65.91-.1.38-.03.78.18 1.09.43.64 1.34.8 2.03.37l2.76-1.73.62.83c.08.1.11.22.09.34s-.09.22-.2.29l-3.67 2.45c-.23.15-.29.46-.14.69s.46.29.69.14l3.67-2.45c.34-.22.56-.56.63-.96.07-.39-.03-.79-.27-1.11l-.9-1.2c-.16-.21-.44-.26-.67-.12l-3.15 1.97c-.23.14-.55.11-.67-.07-.07-.11-.06-.23-.04-.29.03-.12.12-.24.23-.32l3.67-2.45c.26-.17.57-.23.89-.16.39.08.74.34.97.7l3.66 5.85c-1.08.08-2.07.33-3.03.59-1.19.31-2.43.64-3.86.64-3.26 0-5.07-1.41-5.09-1.43-.21-.17-.53-.14-.7.08-.17.21-.14.53.07.7.08.07 2.07 1.65 5.71 1.65 1.56 0 2.86-.34 4.12-.67 1.16-.3 2.25-.59 3.48-.59 2.45 0 4.68 1.62 4.7 1.63.09.07.19.1.29.1.15 0 .31-.07.4-.2.16-.22.12-.54-.11-.7z\"></path></svg></div>";
    amenitiesHtml += "<div class='amenitiesfootertext'>Outdoor Pool</div>";
    amenitiesHtml += "</div>";

    amenitiesHtml += "<div class='amenitiesfooter'>";
    amenitiesHtml += "<div style='float:left;'><svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" class=\"injected-svg brand-ou:text-primary brand-lx:text-brand\" data-src=\"/modules/assets/svgs/icons/regular/gym.svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" role=\"img\" aria-hidden=\"true\" fill=\"currentColor\" width=\"28\" height=\"28\" data-testid=\"icon-gym\"><path d=\"m0 0v24h24v-24z\" fill=\"none\"></path><path d=\"m15 10.5h-6c-.28 0-.5.22-.5.5s.22.5.5.5h6c.28 0 .5-.22.5-.5s-.22-.5-.5-.5z\"></path><path d=\"m15 12.5h-6c-.28 0-.5.22-.5.5s.22.5.5.5h6c.28 0 .5-.22.5-.5s-.22-.5-.5-.5z\"></path><path d=\"m4 15.5c.18 0 .34-.04.5-.09v1.59c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-10c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v1.59c-.16-.06-.32-.09-.5-.09-.83 0-1.5.67-1.5 1.5v4c0 .83.67 1.5 1.5 1.5zm1.5-8.5c0-.28.22-.5.5-.5s.5.22.5.5v10c0 .28-.22.5-.5.5s-.5-.22-.5-.5zm-2 3c0-.28.22-.5.5-.5s.5.22.5.5v4c0 .28-.22.5-.5.5s-.5-.22-.5-.5z\"></path><path d=\"m20 8.5c-.18 0-.34.04-.5.09v-1.59c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v10c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-1.59c.16.06.32.09.5.09.83 0 1.5-.67 1.5-1.5v-4c0-.83-.67-1.5-1.5-1.5zm-1.5 8.5c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-10c0-.28.22-.5.5-.5s.5.22.5.5zm2-3c0 .28-.22.5-.5.5s-.5-.22-.5-.5v-4c0-.28.22-.5.5-.5s.5.22.5.5z\"></path></svg></div>";
    amenitiesHtml += "<div class='amenitiesfootertext'>Fitness Center</div>";
    amenitiesHtml += "</div>";

    amenitiesHtml += "<div class='amenitiesfooter'>";
    amenitiesHtml += "<div style='float:left;'><svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" class=\"injected-svg brand-ou:text-primary brand-lx:text-brand\" data-src=\"/modules/assets/svgs/icons/regular/digital-key.svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" role=\"img\" aria-hidden=\"true\" fill=\"currentColor\" width=\"28\" height=\"28\" data-testid=\"icon-digital-key\"><path d=\"m0 0v24h24v-24z\" fill=\"none\"></path><path d=\"m15 3.5c-3.03 0-5.5 2.47-5.5 5.5 0 .78.17 1.53.47 2.21l-5.88 5.88c-.38.38-.58.88-.58 1.41s.21 1.04.58 1.41c.39.39.9.58 1.41.58s1.02-.2 1.41-.58l.28-.28.43.43c.28.28.66.44 1.06.44s.78-.16 1.06-.44l.71-.71c.58-.58.58-1.54 0-2.12l-.43-.43 1.39-1.39c.2-.2.2-.51 0-.71s-.51-.2-.71 0l-4.5 4.5c-.39.39-1.03.39-1.41 0-.19-.19-.29-.44-.29-.71s.1-.52.29-.71l5.68-5.68c.99 1.44 2.65 2.39 4.53 2.39 3.03 0 5.5-2.47 5.5-5.5s-2.47-5.5-5.5-5.5zm-5.25 14.44c.19.2.19.51 0 .71l-.71.71c-.19.19-.52.19-.71 0l-.43-.43 1.41-1.41.43.43zm5.25-4.44c-2.48 0-4.5-2.02-4.5-4.5s2.02-4.5 4.5-4.5 4.5 2.02 4.5 4.5-2.02 4.5-4.5 4.5z\"></path><path d=\"m16 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 3c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z\"></path></svg></div>";
    amenitiesHtml += "<div class='amenitiesfootertext'>Digital Key</div>";
    amenitiesHtml += "</div>";

    amenitiesHtml += "<div class='amenitiesfooter'>";
    amenitiesHtml += "<div style='float:left;'><svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" class=\"injected-svg brand-ou:text-primary brand-lx:text-brand\" data-src=\"/modules/assets/svgs/icons/regular/concierge.svg\" xmlns:xlink=\"http://www.w3.org/1999/xlink\" role=\"img\" aria-hidden=\"true\" fill=\"currentColor\" width=\"28\" height=\"28\" data-testid=\"icon-concierge\"><path d=\"m0 0v24h24v-24z\" fill=\"none\"></path><circle cx=\"10\" cy=\"17\" r=\".75\"></circle><circle cx=\"10\" cy=\"19.5\" r=\".75\"></circle><circle cx=\"14\" cy=\"17\" r=\".75\"></circle><circle cx=\"14\" cy=\"19.5\" r=\".75\"></circle><path d=\"m11 13.5c-.65 0-1.41-.62-1.65-.85-.14-.14-.36-.19-.54-.11-.19.08-.31.26-.31.46v.53c-2.25.25-4 2.16-4 4.47v2c0 .28.22.5.5.5s.5-.22.5-.5v-2c0-1.93 1.57-3.5 3.5-3.5.27 0 .49-.22.5-.48.29.18.63.34 1 .43v.56c0 .28.22.5.5.5s.5-.22.5-.5v-1c0-.28-.22-.5-.5-.5z\"></path><path d=\"m15.5 13.53v-.53c0-.2-.12-.38-.31-.46s-.4-.04-.54.11c-.24.24-1 .85-1.65.85-.28 0-.5.22-.5.5v1c0 .28.22.5.5.5s.5-.22.5-.5v-.56c.37-.08.71-.25 1-.43 0 .27.23.48.5.48 1.93 0 3.5 1.57 3.5 3.5v2c0 .28.22.5.5.5s.5-.22.5-.5v-2c0-2.31-1.75-4.22-4-4.47z\"></path><path d=\"m9 7h6c.28 0 .5-.22.5-.5 0-.5-.07-3-1.5-3h-4c-1.43 0-1.5 2.5-1.5 3 0 .28.22.5.5.5zm1-2.5h4c.18 0 .41.68.48 1.5h-4.96c.07-.82.3-1.5.48-1.5z\"></path><path d=\"m15.5 8.5c0-.28-.22-.5-.5-.5s-.5.22-.5.5c0 1.65-1.12 3-2.5 3s-2.5-1.35-2.5-3c0-.28-.22-.5-.5-.5s-.5.22-.5.5c0 2.21 1.57 4 3.5 4s3.5-1.79 3.5-4z\"></path></svg></div>";
    amenitiesHtml += "<div class='amenitiesfootertext'>Digital Check-In</div>";
    amenitiesHtml += "</div>";

    amenitiesHtml += "</div>";

    amenitiesHtml += "<a class='amentitiesfooterbutton' target='_blank' href='https://www.hilton.com/en/hotels/pbitrqq-the-ray-hotel-delray-beach/hotel-info/'>VIEW ALL</a>";

    return (
      <Flex sx={{ ...footerNavStyles.base, ...footerNavStyles.amenities }}>
        <Box dangerouslySetInnerHTML={{__html: amenitiesHtml }}>
        </Box>
      </Flex>
    );
  };
  const renderStaticLinks = (links) => {
    return (
      <Flex sx={{ ...footerNavStyles.base, ...footerNavStyles.bottomLinks }}>
        {links?._rawExternalLinksItems.map((item) => (
          <AnchorLink key={Math.random().toString(36).substring(7)} target="__blank" href={item.link.linkUrl} sx={footerNavStyles.link}>
            {item.label}
          </AnchorLink>
        ))}
      </Flex>
    );
  };

  const newsletterEmbed = "<div id='newslettercontainer' style=\"display:block;width:100%;position:relative;top:20px;margin:0px;border-bottom: 1px solid #fff;margin-bottom:30px;\"></div>\n" +
      "\n<style>" +
      "#gdpr_text { display: none !important; }\n" +
      ".ctct-form-header { display: none !important; }\n" +
      ".ctct-form-text { color:#ffffff !important; font-size:12px !important; margin-bottom:5px !important; }\n" +
      ".ctct-form-label { display: none !important; }\n" +
      ".ctct-form-field {" +
      "    float: left !important;" +
      "    display: inline-block !important;\n" +
      "    width: 190px !important;\n" +
      "}\n" +
      ".ctct-form-button {" +
      "    float: left !important;\n" +
      "    display: inline-block !important;\n" +
      "    width: 110px !important;\n" +
      "    margin-left: 5px !important;\n" +
      "    padding: 14px !important;\n" +
      "    background:#c1974b !important;\n" +
      "    color:#ffffff !important;\n" +
      "    font-family:\"Brandon Grotesque\" !important;}" +
      "}\n" +
      ".ctct-form-embed.form_0 .ctct-form-defaults .ctct-form-text { color: #ffffff !important; }\n" +
      ".ctct-inline-form, .ctct-form-defaults, div.ctct-form-embed div.ctct-form-defaults p.ctct-form-text \n{" +
      "background:transparent !important;padding:0px !important;" +
      "font-family:\"Brandon Grotesque\" !important;}" +
      "</style>";

  const [FooterRef, isFooterVisible] = useElementOnScreen();
  const { explore, connect, externalLinks, more } = footerData?.footerNavigationData;
  return (
    <>
      <Box sx={footerStyles.base(isFooterVisible)} as="footer">
        <Box ref={FooterRef} sx={footerStyles.inner(isFooterVisible)}>
          <Box sx={footerStyles.top}>
            <Box sx={footerStyles.topLeft}>
              <ImageFullWidth image={footerData?.footerRaylogo.childImageSharp?.gatsbyImageData} alt="" />
            </Box>
            <Box sx={footerStyles.topRight}>
              {renderAmenities()}
            </Box>
          </Box>
          <Box sx={footerStyles.middle}>
            <Box sx={footerStyles.topLeft}>
            </Box>
            <Box sx={footerStyles.topRight}>
              {explore && renderExploreLinks(explore)}
              {connect && renderConnectLinks(connect)}
              {more && renderMoreLinks(more)}
            </Box>
          </Box>
          <Box dangerouslySetInnerHTML={{__html: newsletterEmbed }}>
          </Box>
          <Flex sx={{ ...footerNavStyles.base, ...footerNavStyles.bottomLinks }}>
            <Text sx={footerNavStyles.text}>
              &copy;
              {new Date().getFullYear()}
              <Text> hilton. all rights reserved</Text>
            </Text>
          </Flex>
            {externalLinks && renderStaticLinks(externalLinks)}
        </Box>
      </Box>
    </>
  );
}

const footerStyles = {
  base: (isFooterVisible) => ({
    pt: [6, null, 8],
    pb: [10, null, '6.5%'],
    position: 'relative',
    zIndex: 1,
    transition: 'background-position 2s ease 1s',
    backgroundPosition: isFooterVisible ? '0 100%' : '0 200%',
    backgroundRepeat: 'no-repeat',
    backgroundColor: 'backgroundInverted',
    backgroundSize: ['auto 136px', null, '100% auto'],
    backgroundImage: `url(${footerBg})`,
  }),
  inner: (isFooterVisible) => ({
    variant: 'styles.container',
    transition: 'opacity 1s ease 0.5s',
    opacity: isFooterVisible ? 1 : 0,
    position: 'relative',
    zIndex: 1,
  }),
  top: {
    pt: 1,
    pb: [0, null, 5],
    borderBottom: `0px`,
    justifyContent: [null, null, 'space-between'],
    display: [null, null, 'flex'],
  },
  middle: {
    pt: 1,
    pb: [0, null, 5],
    borderBottom: `1px solid${colors.WHITE}`,
    justifyContent: [null, null, 'space-between'],
    display: [null, null, 'flex'],
  },
  topLeft: {
    maxWidth: ['185px', null, '212px'],
    mx: ['auto', null, '0'],
    mb: [5, null, '0'],
    width: '100%',
  },
  topRight: {
    width: [null, null, 720],
    justifyContent: 'space-between',
    display: [null, null, 'flex'],
  },
};

const footerNavStyles = {
  base: {
    justifyContent: 'space-between',
    py: [2, null, 4],
    color: 'textInverted',
    fontSize: 1,
    textTransform: 'uppercase',
    letterSpacing: 5,
    lineHeight: '24px',
    textAlign: ['center', null, 'left'],
  },
  heading: {
    fontSize: 1,
    mb: [3, '24px', null],
    fontFamily: 'body',
    fontWeight: 'bold',
  },
  item: {
    py: 1,
    letterSpacing: 7,
  },
  text: {
    color: 'inherit',
    textTransform: 'inherit',
    letterSpacing: 5,
  },
  link: {
    color: 'textInverted',
    textDecoration: 'none',
  },
  anchor: {
    color: 'textInverted',
    letterSpacing: 5,
  },
  social: {
    py: 4,
  },
  socialLinks: {
    border: `1px solid${colors.WHITE}`,
    color: 'textInverted',
    width: 32,
    height: 32,
    display: 'inline-flex',
    borderRadius: '50%',
    ml: [2, null, 0],
    mr: [2, null, 3],
    variant: 'styles.flexCenter',
    transition: '0.2s ease',
    '&:hover': {
      bg: 'background',
      color: 'text',
    },
  },
  socialIcon: {
    fill: 'currentColor',
  },
  bottomLinks: {
    flexDirection: ['column', null, 'row'],
    py: [4, null, null],
    '> a': {
      mt: [3, 0, 0],
    },
  },
  amenities: {
    flexDirection: ['column', null, 'row'],
    py: [4, null, null],
    '> a': {
      mt: [3, 0, 0],
    },
  },
};
